import {
  ROTULO_TEMPERATURA,
  TEMPERATURAS,
  type CardPainel,
  type Temperatura,
} from '@baiocchi/shared'
import {
  Botao,
  Campo,
  Cartao,
  Entrada,
  Esqueleto,
  Etiqueta,
  Selecao,
  cn,
} from '@baiocchi/ui'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { chaves } from '../api/chaves'
import { painelApi } from '../api/leads'
import { useSessao } from '../autenticacao/contexto-sessao'
import { EstadoDeErro } from '../componentes/estados-tela'
import { EtiquetaAbordagem, EtiquetaResponsavel } from '../componentes/etiquetas-dominio'
import { IconeBusca, IconeMais } from '../componentes/icones'
import { COR_TEMPERATURA, TOM_TEMPERATURA } from '../dominio/rotulos'
import { useDebounce } from '../ganchos/usar-debounce'
import { useFiltrosUrl } from '../ganchos/usar-filtros-url'
import { opcoesDeReferencia, useEquipes } from '../ganchos/usar-catalogos'
import { CabecalhoPagina } from '../layout/layout-app'
import { diasAtras, formatarTempoRelativo, hojeFiltro } from '../utilitarios/formatar'

const ATALHOS = [
  { rotulo: 'Hoje', dias: 0 },
  { rotulo: '7 dias', dias: 7 },
  { rotulo: '30 dias', dias: 30 },
  { rotulo: '90 dias', dias: 90 },
  { rotulo: 'Vitalício', dias: null },
] as const

/**
 * TELA-02 — Painel de atendimentos. A tela de trabalho diário.
 *
 * Colunas por temperatura, com contador no cabeçalho. **Uma chamada por coluna**,
 * de propósito (RNF-73: p95 < 1 s por coluna): a tela pinta conforme chega, em vez
 * de esperar a coluna mais cheia — que no legado tem 254 cards.
 *
 * A temperatura é **derivada** pelo servidor (RN-21) e nunca escolhida pelo
 * usuário: não há como arrastar um card entre colunas. Mover cliente entre colunas
 * seria editar uma conclusão do sistema, e a conclusão vem de status, agendamento,
 * tempo sem interação e tentativas de contato.
 *
 * Todas as temperaturas aparecem, **inclusive com zero**: coluna que desaparece
 * quando zera faz a tela pular de posição a cada atualização.
 */
export function PaginaPainelAtendimentos() {
  const { filtros, definir, limpar } = useFiltrosUrl()
  const navegar = useNavigate()
  const { pode } = useSessao()

  const [buscaLocal, setBuscaLocal] = useState(filtros.busca ?? '')
  const buscaAtrasada = useDebounce(buscaLocal)
  const equipes = useEquipes(filtros.unidadeId)

  // Só `buscaAtrasada`: o efeito escreve em `filtros.busca` e reagir à própria
  // escrita o faria disparar duas vezes por letra digitada.
  useEffect(() => {
    if ((filtros.busca ?? '') !== buscaAtrasada) definir({ busca: buscaAtrasada || undefined })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buscaAtrasada])

  const filtrosPainel = {
    de: filtros.de,
    ate: filtros.ate,
    busca: filtros.busca,
    equipeId: filtros.equipeId,
    responsavelUuid: filtros.responsavelUuid,
    apenasSemResponsavel: filtros.apenasSemResponsavel,
    apenasAtrasados: filtros.apenasAtrasados,
  }

  const colunas = useQuery({
    queryKey: chaves.painel.colunas(filtrosPainel),
    queryFn: ({ signal }) => painelApi.colunas(filtrosPainel, signal),
    staleTime: 15_000,
  })

  const atalhoAtivo = (() => {
    if (!filtros.de && !filtros.ate) return 'Vitalício'
    for (const atalho of ATALHOS) {
      if (atalho.dias === null) continue
      const de = atalho.dias === 0 ? hojeFiltro() : diasAtras(atalho.dias)
      if (filtros.de === de && filtros.ate === hojeFiltro()) return atalho.rotulo
    }
    return null
  })()

  const totalPorTemperatura = new Map(
    (colunas.data?.dados ?? []).map((coluna) => [coluna.temperatura, coluna.total]),
  )

  return (
    <>
      <CabecalhoPagina
        titulo="Atendimentos"
        descricao="Fila de trabalho por temperatura. A temperatura é calculada pelo sistema."
        acoes={
          pode('LEAD_CRIAR') ? (
            <Botao
              onClick={() => navegar('/leads/novo')}
              iconeEsquerda={<IconeMais className="h-4 w-4" />}
            >
              Novo lead
            </Botao>
          ) : null
        }
      />

      <Cartao className="mb-5">
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-1.5">
            {ATALHOS.map((atalho) => (
              <button
                key={atalho.rotulo}
                type="button"
                aria-pressed={atalhoAtivo === atalho.rotulo}
                onClick={() =>
                  definir(
                    atalho.dias === null
                      ? { de: undefined, ate: undefined }
                      : {
                          de: atalho.dias === 0 ? hojeFiltro() : diasAtras(atalho.dias),
                          ate: hojeFiltro(),
                        },
                  )
                }
                className={cn(
                  'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                  atalhoAtivo === atalho.rotulo
                    ? 'border-marca-500 bg-marca-500 text-white'
                    : 'border-borda-forte bg-superficie text-conteudo-suave hover:bg-superficie-2',
                )}
              >
                {atalho.rotulo}
              </button>
            ))}
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Campo rotulo="Busca">
              {(atributos) => (
                <Entrada
                  {...atributos}
                  value={buscaLocal}
                  placeholder="Nome, telefone ou e-mail"
                  prefixo={<IconeBusca className="h-4 w-4" />}
                  onChange={(evento) => setBuscaLocal(evento.target.value)}
                />
              )}
            </Campo>

            <Campo rotulo="Equipe">
              {(atributos) => (
                <Selecao
                  {...atributos}
                  vazio="Todas as equipes"
                  value={filtros.equipeId ?? ''}
                  opcoes={opcoesDeReferencia(equipes.data?.dados)}
                  onChange={(evento) => definir({ equipeId: evento.target.value || undefined })}
                />
              )}
            </Campo>

            <Campo rotulo="De">
              {(atributos) => (
                <Entrada
                  {...atributos}
                  type="date"
                  value={filtros.de ?? ''}
                  onChange={(evento) => definir({ de: evento.target.value || undefined })}
                />
              )}
            </Campo>

            <Campo rotulo="Até">
              {(atributos) => (
                <Entrada
                  {...atributos}
                  type="date"
                  value={filtros.ate ?? ''}
                  onChange={(evento) => definir({ ate: evento.target.value || undefined })}
                />
              )}
            </Campo>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <ChaveFiltro
              rotulo="Só sem responsável"
              ativo={filtros.apenasSemResponsavel === 'true'}
              onAlternar={(ativo) => definir({ apenasSemResponsavel: ativo ? 'true' : undefined })}
            />
            <ChaveFiltro
              rotulo="Só atrasados"
              ativo={filtros.apenasAtrasados === 'true'}
              onAlternar={(ativo) => definir({ apenasAtrasados: ativo ? 'true' : undefined })}
            />
            <Botao
              variante="fantasma"
              tamanho="sm"
              onClick={() => {
                limpar()
                setBuscaLocal('')
              }}
            >
              Limpar
            </Botao>
          </div>
        </div>
      </Cartao>

      {colunas.isError ? (
        <EstadoDeErro erro={colunas.error} aoTentarNovamente={() => void colunas.refetch()} />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2 2xl:grid-cols-4">
          {TEMPERATURAS.map((temperatura) => (
            <ColunaPainel
              key={temperatura}
              temperatura={temperatura}
              total={totalPorTemperatura.get(temperatura) ?? 0}
              carregandoTotal={colunas.isPending}
              filtros={filtrosPainel}
            />
          ))}
        </div>
      )}
    </>
  )
}

function ChaveFiltro({
  rotulo,
  ativo,
  onAlternar,
}: {
  rotulo: string
  ativo: boolean
  onAlternar: (ativo: boolean) => void
}) {
  return (
    <button
      type="button"
      aria-pressed={ativo}
      onClick={() => onAlternar(!ativo)}
      className={cn(
        'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
        ativo
          ? 'border-marca-500 bg-marca-500 text-white'
          : 'border-borda-forte bg-superficie text-conteudo-suave hover:bg-superficie-2',
      )}
    >
      {rotulo}
    </button>
  )
}

/**
 * Uma coluna do painel.
 *
 * Rolagem própria com carregamento incremental por cursor. A coluna do legado com
 * 254 cards não caberia numa página só, e paginar por número faria o operador
 * perder o lugar quando um lead novo entrasse no topo — que é o problema que o
 * cursor keyset resolve.
 */
function ColunaPainel({
  temperatura,
  total,
  carregandoTotal,
  filtros,
}: {
  temperatura: Temperatura
  total: number
  carregandoTotal: boolean
  filtros: Record<string, string | undefined>
}) {
  const consulta = useInfiniteQuery({
    queryKey: chaves.painel.cards(temperatura, filtros),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      painelApi.cards(temperatura, { ...filtros, limite: 25, cursor: pageParam }, signal),
    getNextPageParam: (ultima) => ultima.paginacao.proximoCursor ?? undefined,
    staleTime: 15_000,
  })

  const cards = consulta.data?.pages.flatMap((pagina) => pagina.dados) ?? []

  return (
    <section className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-borda bg-superficie">
      <header
        className="flex items-center justify-between gap-2 px-3 py-2.5"
        style={{ borderTop: `3px solid ${COR_TEMPERATURA[temperatura]}` }}
      >
        <h2 className="text-sm font-semibold text-conteudo">
          Leads {ROTULO_TEMPERATURA[temperatura].toLowerCase()}
        </h2>
        {carregandoTotal ? (
          <Esqueleto className="h-6 w-9" />
        ) : (
          <Etiqueta tom={TOM_TEMPERATURA[temperatura]} tamanho="md">
            {total}
          </Etiqueta>
        )}
      </header>

      <div className="rolagem-fina max-h-[32rem] min-h-0 flex-1 overflow-y-auto border-t border-borda bg-superficie-2 p-2">
        {consulta.isPending ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }, (_, indice) => (
              <Esqueleto key={indice} className="h-24 w-full" />
            ))}
          </div>
        ) : consulta.isError ? (
          <p className="px-2 py-6 text-center text-xs text-perigo">
            Não foi possível carregar esta coluna.{' '}
            <button
              type="button"
              onClick={() => void consulta.refetch()}
              className="font-medium underline"
            >
              Tentar novamente
            </button>
          </p>
        ) : cards.length === 0 ? (
          <p className="px-2 py-8 text-center text-xs text-conteudo-fraco">
            Nenhum lead nesta coluna.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {cards.map((card) => (
              <li key={card.uuid}>
                <CartaoDoPainel card={card} />
              </li>
            ))}
          </ul>
        )}

        {consulta.hasNextPage && (
          <div className="mt-2 flex justify-center">
            <Botao
              variante="fantasma"
              tamanho="sm"
              carregando={consulta.isFetchingNextPage}
              onClick={() => void consulta.fetchNextPage()}
            >
              Carregar mais
            </Botao>
          </div>
        )}
      </div>
    </section>
  )
}

function CartaoDoPainel({ card }: { card: CardPainel }) {
  return (
    <Link
      to={`/leads/${card.uuid}`}
      className={cn(
        'flex flex-col gap-2 rounded-md border p-2.5 shadow-sm transition-colors',
        // Realce de atenção: retorno vencido (RN-25A). Derivado no servidor.
        card.atrasado
          ? 'border-perigo/40 bg-perigo-suave hover:border-perigo/60'
          : 'border-borda bg-superficie hover:border-marca-300',
      )}
    >
      <p className="truncate text-sm font-medium text-conteudo">{card.clienteNome}</p>

      <div className="flex flex-wrap items-center gap-1">
        <EtiquetaResponsavel responsavel={card.responsavel} />
        <EtiquetaAbordagem tentativas={card.tentativasContato} />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-1.5 text-[0.6875rem] text-conteudo-fraco">
        <span>
          {card.ultimaInteracaoEm
            ? `interação ${formatarTempoRelativo(card.ultimaInteracaoEm)}`
            : 'nunca atendido'}
        </span>
        {card.proximoRetornoEm ? (
          <span className={cn('tabular-nums', card.atrasado && 'font-semibold text-perigo')}>
            retorno {formatarTempoRelativo(card.proximoRetornoEm)}
          </span>
        ) : null}
      </div>

      {card.equipe ? (
        <span className="text-[0.6875rem] text-conteudo-fraco">{card.equipe.titulo}</span>
      ) : null}
    </Link>
  )
}
